import React, { useState } from "react";
import { Modal, Radio, Space, Form, DatePicker, Popover, Button } from "antd";
import type { RadioChangeEvent } from "antd";
import { useDispatch, useSelector } from "react-redux";
import {
    updateDailyBudgetModalData,
    updateDailyBudgetReviewModalDisplay,
    updateDailyBudgetUpdatePayload,
} from "@features/modalSlice";
import dayjs, { Dayjs } from "dayjs";
import { InfoCircleOutlined } from "@ant-design/icons";
import { showToast } from "@/ts/modals";
import {
    AppDispatch,
    Campaign,
    DailyBudgetConfigUpdate,
} from "@/ts/common-types";
import { updateDailyBudgetConfig } from "@/ts/retrieve-data";
import $ from "jquery";
import { isDisabledDate } from "@/ts/utils";

interface IDailyBudgetReviewModalProps {
    showDailyBudgetReviewModal: boolean;
}

const DailyBudgetReviewModal: React.FC<IDailyBudgetReviewModalProps> = ({
    showDailyBudgetReviewModal,
}) => {
    const dailyBudgetModalData = useSelector(
        (state: any) => state.dailyBudgetModalData.value
    );
    const [radioValue, setRadioValue] = useState<string>("current");
    const [duration, setDuration] = useState<Dayjs | null>(dayjs());
    const dispatch = useDispatch<AppDispatch>();
    const handleClose = () => {
        dispatch(updateDailyBudgetReviewModalDisplay(false));
        dispatch(updateDailyBudgetModalData({} as Campaign));
    };

    const handleSubmit = async () => {
        if (radioValue === "period" && !duration) {
            showToast("danger", "Please select a date");
        } else {
            const data: DailyBudgetConfigUpdate = {
                campaigns: [dailyBudgetModalData.sys_campaign_id],
                updateType: radioValue,
                updateValue:
                    radioValue === "permanently"
                        ? "0"
                        : radioValue === "current"
                        ? dayjs().format("YYYY-MM-DD")
                        : duration.format("YYYY-MM-DD"),
            };
            dispatch(updateDailyBudgetUpdatePayload(data));
            const { payload } = await dispatch(updateDailyBudgetConfig());
            if (payload.status && radioValue === "permanently") {
                $(
                    `#dailyBudgetModalBtn_${dailyBudgetModalData.sys_campaign_id}`
                ).css("color", "#B2B2B2");
            }

            dispatch(updateDailyBudgetReviewModalDisplay(false));
            dispatch(updateDailyBudgetModalData({} as Campaign));
        }
    };

    const onChange = (e: RadioChangeEvent) => {
        setRadioValue(e.target.value);
    };

    const handleOpenChange = (open: boolean) => {
        if (open && !duration) {
            setDuration(null);
        }
    };

    const handleDateChange = (date: Dayjs | null) => {
        setDuration(date === null ? null : dayjs(date));
    };

    const ModalHeaderElement = () => (
        <div className="d-flex align-items-center my-1 pb-3">
            <div>Schedule Daily Budget</div>
            <Popover
                className="mx-2"
                title="If disabled for a period, daily budget will be applied back at the start of the next day"
                styles={{
                    body: { padding: "10px", width: "250px" },
                }}
                placement="right"
            >
                <InfoCircleOutlined />
            </Popover>
        </div>
    );

    return (
        <Modal
            centered
            open={showDailyBudgetReviewModal}
            title={<ModalHeaderElement />}
            onOk={handleClose}
            onCancel={handleClose}
            width="30vw"
            styles={{
                body: {
                    height: "16vh",
                    overflowY: "auto",
                },
            }}
            footer={[
                <Button type="primary" key="submit" onClick={handleSubmit}>
                    Submit
                </Button>,
                <Button key="cancel" onClick={handleClose}>
                    Cancel
                </Button>,
            ]}
        >
            <div style={{ height: "100%" }}>
                <Form>
                    <div className="fs-6 fw-bold">
                        Daily budget has been setup for the campaign(s). What do
                        you want to do?
                    </div>
                    <Form.Item>
                        <div className="my-3">
                            <Radio.Group onChange={onChange} value={radioValue}>
                                <Space direction="vertical">
                                    <Radio value={"current"}>
                                        Disable for current day
                                    </Radio>
                                    <Radio value={"permanently"}>
                                        Disable permanently
                                    </Radio>
                                    <div className="d-flex align-items-center">
                                        <Radio value={"period"}>
                                            Disable for a period
                                        </Radio>
                                        <DatePicker
                                            name="disable_till"
                                            disabled={radioValue !== "period"}
                                            disabledDate={isDisabledDate}
                                            allowClear={true}
                                            defaultValue={dayjs()}
                                            onOpenChange={handleOpenChange}
                                            onChange={handleDateChange}
                                        />
                                    </div>
                                </Space>
                            </Radio.Group>
                        </div>
                    </Form.Item>
                </Form>
            </div>
        </Modal>
    );
};

export default DailyBudgetReviewModal;
